import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import DeviceHubRoundedIcon from "@mui/icons-material/DeviceHubRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import SettingsEthernetRoundedIcon from "@mui/icons-material/SettingsEthernetRounded";
import { APP_API_BASE, APP_API_V1_BASE } from "../../../config/api";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";

const ONEM2M_API = `${APP_API_V1_BASE}/onem2m`;

const OPERATION_LABELS = {
  1: "CREATE",
  2: "RETRIEVE",
  3: "UPDATE",
  4: "DELETE",
  5: "NOTIFY",
};

const MCA_ACTIONS = [
  {
    value: "CREATE_AE",
    label: "Create / confirm AE",
    helper: "Registers the Application Entity below the CSEBase.",
  },
  {
    value: "CREATE_CONTAINER",
    label: "Create test container",
    helper: "Creates a child telemetry container under the AE.",
  },
  {
    value: "CREATE_CIN",
    label: "Create content instance",
    helper: "Posts sample telemetry into the selected container.",
  },
  {
    value: "RETRIEVE",
    label: "Retrieve target",
    helper: "Reads a resource by RI/path using oneM2M headers.",
  },
  {
    value: "UPDATE_CONTAINER",
    label: "Update container labels",
    helper: "Tests the UPDATE primitive against a container resource.",
  },
  {
    value: "DELETE_RESOURCE",
    label: "Delete target resource",
    helper: "Advanced destructive test. Requires explicit target.",
  },
];

const DEFAULT_PROFILE_FORM = {
  provider_name: "",
  provider_code: "",
  app_id: "Ntrackcore.tenant.default",
  cse_id: "/in-cse",
  cse_base_resource_id: "in-cse",
  cse_base_resource_name: "in-cse",
  registrar_url: APP_API_BASE,
  registrar_mqtt_url: "mqtts://mqtts.trackcoredata.com:8883",
  security_mode: "CERT_TLS",
  service_state: "active",
};

const DEFAULT_MCA_PAYLOAD = JSON.stringify(
  {
    packet_type: "dashboard_oneM2M_smoke",
    ts: "auto",
    flow_lpm: 120.5,
    pressure_bar: 2.4,
    pump_state: "RUNNING",
  },
  null,
  2,
);

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function compact(value, start = 10, end = 6) {
  if (!value) return "-";
  const text = String(value);
  if (text.length <= start + end + 3) return text;
  return `${text.slice(0, start)}...${text.slice(-end)}`;
}

function parseMqttUrl(value) {
  if (!value)
    return { host: "mqtts.trackcoredata.com", port: "8883", tls: true };
  try {
    const parsed = new URL(value);
    const tls = parsed.protocol.includes("mqtts");
    return {
      host: parsed.hostname || value,
      port: parsed.port || (tls ? "8883" : "1883"),
      tls,
    };
  } catch {
    return { host: value, port: "1883", tls: false };
  }
}

function cseName(value) {
  return String(value || "in-cse").replace(/^\/+/, "") || "in-cse";
}

function buildHeaders(token, activeOrg) {
  return {
    Authorization: `Bearer ${token}`,
    ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
  };
}

function cleanPayload(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(
      ([, value]) => value !== "" && value !== undefined,
    ),
  );
}

function defaultAppId(activeOrg) {
  const token = String(activeOrg?.code || activeOrg?.name || "tenant")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `Ntrackcore.${token || "tenant"}.default`;
}

function getErrorMessage(error) {
  const response = error?.response?.data;
  const oneM2MDebug = response?.pc?.["m2m:dbg"];
  return (
    oneM2MDebug ||
    response?.error ||
    response?.message ||
    error?.message ||
    "Something went wrong."
  );
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch (error) {
    throw new Error("Sample payload must be valid JSON.");
  }
}

function requestId(prefix = "ui") {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function bundleTopic(bundle) {
  return (
    bundle?.config?.oneM2M?.mca_mqtt_request_topic ||
    bundle?.config?.protocols?.oneM2M?.mca_mqtt_request_topic ||
    ""
  );
}

function bundleHttpPath(bundle) {
  return (
    bundle?.config?.oneM2M?.mca_http_path ||
    bundle?.config?.protocols?.oneM2M?.mca_http_path ||
    `/api/v1/onem2m/mca/${bundle?.default_container_resource_id || ""}`
  );
}

function deviceLabel(device) {
  const asset = device?.inventory_asset?.serial_number;
  const type =
    device?.device_type?.name || device?.device_type || device?.protocol;
  return [device?.name || "Unnamed device", asset, type]
    .filter(Boolean)
    .join(" / ");
}

function profileLabel(profile) {
  return [profile?.provider_name, profile?.app_id].filter(Boolean).join(" / ");
}

function statusChip(status, goodStatuses = ["active", "ok", "issued"]) {
  const normalized = String(status || "unknown").toLowerCase();
  const good = goodStatuses.includes(normalized);
  return (
    <Chip
      size="small"
      label={String(status || "unknown").toUpperCase()}
      sx={{
        fontWeight: 800,
        letterSpacing: "0.04em",
        color: good ? "#166534" : "#92400E",
        bgcolor: good ? "rgba(22,163,74,0.12)" : "rgba(245,158,11,0.14)",
        border: "1px solid rgba(148,163,184,0.22)",
      }}
    />
  );
}

function MetricCard({ icon, label, value, helper, tone = "#2563EB" }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.2,
        borderRadius: 4,
        border: "1px solid rgba(148,163,184,0.22)",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,250,252,0.86))",
        minHeight: 128,
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            color: tone,
            bgcolor: `${tone}18`,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ color: "#64748B", fontWeight: 800, fontSize: 13 }}>
            {label}
          </Typography>
          <Typography sx={{ color: "#0F172A", fontWeight: 900, fontSize: 34 }}>
            {Number(value || 0).toLocaleString()}
          </Typography>
          <Typography sx={{ color: "#64748B", fontSize: 13 }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function EmptyState({ title, body }) {
  return (
    <Box
      sx={{
        p: 4,
        textAlign: "center",
        border: "1px dashed rgba(100,116,139,0.32)",
        borderRadius: 4,
        bgcolor: "#F8FAFC",
      }}
    >
      <Typography sx={{ fontWeight: 900, color: "#0F172A", mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ color: "#64748B" }}>{body}</Typography>
    </Box>
  );
}

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : label}>
      <IconButton size="small" onClick={handleCopy} disabled={!text}>
        {copied ? (
          <CheckCircleRoundedIcon fontSize="small" sx={{ color: "#16A34A" }} />
        ) : (
          <ContentCopyRoundedIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
}

function CodeBlock({ value }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        borderRadius: 3,
        bgcolor: "#020617",
        color: "#E2E8F0",
        fontSize: 13,
        overflow: "auto",
        border: "1px solid rgba(15,23,42,0.5)",
      }}
    >
      {value}
    </Box>
  );
}

function ActionCard({ icon, eyebrow, title, body, children, footer }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid rgba(148,163,184,0.24)",
        bgcolor: "#FFFFFF",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              color: "#0F766E",
              bgcolor: "rgba(20,184,166,0.12)",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              sx={{
                color: "#64748B",
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              {eyebrow}
            </Typography>
            <Typography
              sx={{ fontWeight: 950, fontSize: 22, color: "#0F172A" }}
            >
              {title}
            </Typography>
            {body ? (
              <Typography sx={{ color: "#64748B", mt: 0.4 }}>{body}</Typography>
            ) : null}
          </Box>
        </Stack>
        {children}
        {footer}
      </Stack>
    </Paper>
  );
}

function FieldGrid({ children, columns = 2 }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: `repeat(${columns}, minmax(0, 1fr))`,
        },
        gap: 1.4,
      }}
    >
      {children}
    </Box>
  );
}

export default function CommonServiceLayer() {
  const { activeOrg, permissions } = useAuth();
  const token = localStorage.getItem("authToken");
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mcaResult, setMcaResult] = useState(null);
  const [data, setData] = useState({
    health: null,
    profiles: [],
    resources: [],
    bundles: [],
    certificates: [],
    logs: [],
    devices: [],
  });
  const [profileForm, setProfileForm] = useState(DEFAULT_PROFILE_FORM);
  const [bundleForm, setBundleForm] = useState({
    device_id: "",
    service_profile_id: "",
    config_version: "v1",
    ae_id: "",
    default_container_name: "telemetry",
    force_new: "false",
  });
  const [certForm, setCertForm] = useState({
    bundle_id: "",
    ae_id: "",
    subject_alt_name: "",
    service_profile_id: "",
    device_id: "",
    certificate_serial_number: "",
    fingerprint_sha256: "",
    expires_at: "",
    status: "issued",
  });
  const [mcaForm, setMcaForm] = useState({
    bundle_id: "",
    operation: "CREATE_CIN",
    target: "",
    container_name: "telemetry-ui",
    content_json: DEFAULT_MCA_PAYLOAD,
  });

  const headers = useMemo(
    () => buildHeaders(token, activeOrg),
    [activeOrg, token],
  );
  const canManageOrg = can(permissions, "organization.update");

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");

    const requestHeaders = buildHeaders(token, activeOrg);
    const requests = {
      health: axios.get(`${ONEM2M_API}/health`, {
        headers: requestHeaders,
        timeout: 12000,
      }),
      profiles: axios.get(`${ONEM2M_API}/service-profiles`, {
        headers: requestHeaders,
        timeout: 12000,
      }),
      resources: axios.get(`${ONEM2M_API}/resources`, {
        headers: requestHeaders,
        timeout: 12000,
      }),
      bundles: axios.get(`${ONEM2M_API}/config-bundles?active_only=false`, {
        headers: requestHeaders,
        timeout: 12000,
      }),
      certificates: axios.get(`${ONEM2M_API}/certificates`, {
        headers: requestHeaders,
        timeout: 12000,
      }),
      logs: axios.get(`${ONEM2M_API}/request-logs?limit=100`, {
        headers: requestHeaders,
        timeout: 12000,
      }),
      devices: axios.get(`${ONEM2M_API}/eligible-devices`, {
        headers: requestHeaders,
        timeout: 12000,
      }),
    };

    const entries = Object.entries(requests);
    const results = await Promise.allSettled(
      entries.map(([, request]) => request),
    );
    const nextData = {};
    const failures = [];

    results.forEach((result, index) => {
      const key = entries[index][0];
      if (result.status === "fulfilled") {
        const body = result.value.data;
        nextData[key] =
          key === "health"
            ? body
            : Array.isArray(body)
              ? body
              : body?.data || [];
      } else {
        failures.push(key);
        nextData[key] = key === "health" ? null : [];
      }
    });

    setData(nextData);
    if (failures.length) {
      setError(
        `Unable to load ${failures.join(", ")}. Check backend route and permissions.`,
      );
    }
    setLoading(false);
  }, [activeOrg, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!activeOrg) return;
    setProfileForm((previous) => {
      const appIdWasDefault =
        !previous.app_id || previous.app_id === "Ntrackcore.tenant.default";
      return {
        ...previous,
        provider_name: previous.provider_name || activeOrg.name || "",
        provider_code: previous.provider_code || activeOrg.code || "",
        app_id: appIdWasDefault ? defaultAppId(activeOrg) : previous.app_id,
      };
    });
  }, [activeOrg]);

  useEffect(() => {
    setBundleForm((previous) => {
      const next = {
        ...previous,
        device_id: previous.device_id || data.devices?.[0]?.id || "",
        service_profile_id:
          previous.service_profile_id || data.profiles?.[0]?.id || "",
      };
      return next.device_id === previous.device_id &&
        next.service_profile_id === previous.service_profile_id
        ? previous
        : next;
    });
  }, [data.devices, data.profiles]);

  useEffect(() => {
    const latestBundle = data.bundles?.[0];
    if (!latestBundle) return;
    setCertForm((previous) => {
      if (previous.bundle_id && previous.ae_id) return previous;
      const bundle =
        data.bundles.find((candidate) => candidate.id === previous.bundle_id) ||
        latestBundle;
      return {
        ...previous,
        bundle_id: bundle.id,
        ae_id: bundle.ae_id || "",
        subject_alt_name: bundle.subject_alt_name || "",
        service_profile_id: bundle.service_profile_id || "",
        device_id: bundle.device_id || "",
      };
    });
    setMcaForm((previous) => {
      if (previous.bundle_id && previous.target) return previous;
      const bundle =
        data.bundles.find((candidate) => candidate.id === previous.bundle_id) ||
        latestBundle;
      return {
        ...previous,
        bundle_id: bundle.id,
        target: bundle.default_container_resource_id || "",
      };
    });
  }, [data.bundles]);

  const metrics = useMemo(() => {
    const resources = data.resources || [];
    const logs = data.logs || [];
    const bundles = data.bundles || [];
    return {
      activeProfiles: (data.profiles || []).filter(
        (profile) => String(profile.service_state).toLowerCase() === "active",
      ).length,
      activeBundles: bundles.filter((bundle) => bundle.is_active).length,
      aeResources: resources.filter((resource) => resource.resource_type === 2)
        .length,
      cinResources: resources.filter((resource) => resource.resource_type === 4)
        .length,
      failedRequests: logs.filter(
        (log) =>
          Number(log.response_status_code || 0) >= 4000 ||
          Number(log.http_status_code || 0) >= 400,
      ).length,
      devices: data.devices?.length || 0,
    };
  }, [data]);

  const latestBundle = data.bundles?.[0];
  const latestMqtt = parseMqttUrl(latestBundle?.registrar_mqtt_url);
  const simulatorCommand = latestBundle
    ? [
        "python services/onem2m_device_simulator.py",
        `--broker-host ${latestMqtt.host}`,
        `--broker-port ${latestMqtt.port}`,
        latestMqtt.tls ? "--tls" : "",
        `--ae-id ${latestBundle.ae_id}`,
        `--cse-id ${cseName(latestBundle.registrar_cse_id)}`,
        `--container ${
          latestBundle.default_container_resource_id ||
          latestBundle.default_container_name ||
          "telemetry"
        }`,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const httpSmokeCommand = latestBundle
    ? `python scripts/onem2m_smoke_test.py --base-url ${
        latestBundle.registrar_url || APP_API_BASE
      } --origin ${latestBundle.ae_id}`
    : "";

  const setFormValue = (setter, key) => (event) => {
    setter((previous) => ({ ...previous, [key]: event.target.value }));
  };

  const runAction = async (label, action) => {
    setActionBusy(label);
    setError("");
    setSuccess("");
    try {
      await action();
    } catch (actionError) {
      setError(getErrorMessage(actionError));
    } finally {
      setActionBusy("");
    }
  };

  const selectedBundle = (bundleId) =>
    (data.bundles || []).find((bundle) => bundle.id === bundleId);

  const applyBundleToCert = (bundleId) => {
    const bundle = selectedBundle(bundleId);
    setCertForm((previous) => ({
      ...previous,
      bundle_id: bundleId,
      ae_id: bundle?.ae_id || "",
      subject_alt_name: bundle?.subject_alt_name || "",
      service_profile_id: bundle?.service_profile_id || "",
      device_id: bundle?.device_id || "",
    }));
  };

  const applyBundleToMca = (bundleId) => {
    const bundle = selectedBundle(bundleId);
    setMcaForm((previous) => ({
      ...previous,
      bundle_id: bundleId,
      target: bundle?.default_container_resource_id || previous.target || "",
    }));
  };

  const handleCreateProfile = () =>
    runAction("profile", async () => {
      if (!profileForm.app_id) throw new Error("App-ID is required.");
      const payload = cleanPayload({
        ...profileForm,
        organization_id: activeOrg?.id,
        metadata: {
          source: "dashboard_oneM2M_console",
          release_version: data.health?.release_version || "3",
        },
      });
      const response = await axios.post(
        `${ONEM2M_API}/service-profiles`,
        payload,
        {
          headers,
          timeout: 12000,
        },
      );
      setSuccess(
        `Service profile created for ${response.data?.data?.app_id || payload.app_id}.`,
      );
      await loadData();
    });

  const handleGenerateBundle = () =>
    runAction("bundle", async () => {
      if (!bundleForm.device_id) throw new Error("Select a device first.");
      const payload = cleanPayload({
        service_profile_id: bundleForm.service_profile_id,
        config_version: bundleForm.config_version || "v1",
        ae_id: bundleForm.ae_id,
        default_container_name:
          bundleForm.default_container_name || "telemetry",
        force_new: bundleForm.force_new === "true",
        labels: ["trackcore", "onem2m", "dashboard-generated"],
      });
      const response = await axios.post(
        `${ONEM2M_API}/devices/${bundleForm.device_id}/config-bundle`,
        payload,
        { headers, timeout: 16000 },
      );
      const bundle = response.data?.data;
      if (bundle?.id) {
        setCertForm((previous) => ({
          ...previous,
          bundle_id: bundle.id,
          ae_id: bundle.ae_id || "",
          subject_alt_name: bundle.subject_alt_name || "",
          service_profile_id: bundle.service_profile_id || "",
          device_id: bundle.device_id || "",
        }));
        setMcaForm((previous) => ({
          ...previous,
          bundle_id: bundle.id,
          target: bundle.default_container_resource_id || "",
        }));
      }
      setSuccess(
        `Config bundle ${response.data?.status || "saved"} for ${bundle?.ae_id || "device"}.`,
      );
      await loadData();
    });

  const handleRegisterCertificate = () =>
    runAction("certificate", async () => {
      const payload = cleanPayload({
        organization_id: activeOrg?.id,
        service_profile_id: certForm.service_profile_id,
        device_id: certForm.device_id,
        ae_id: certForm.ae_id,
        subject_alt_name: certForm.subject_alt_name,
        certificate_serial_number: certForm.certificate_serial_number,
        fingerprint_sha256: certForm.fingerprint_sha256,
        expires_at: certForm.expires_at,
        status: certForm.status || "issued",
        metadata: {
          source: "dashboard_oneM2M_console",
          bundle_id: certForm.bundle_id,
        },
      });
      if (!payload.ae_id || !payload.subject_alt_name) {
        throw new Error("AE-ID and Subject Alt Name are required.");
      }
      await axios.post(`${ONEM2M_API}/certificates`, payload, {
        headers,
        timeout: 12000,
      });
      setSuccess(`Certificate metadata registered for ${payload.ae_id}.`);
      await loadData();
    });

  const handleRunMca = () =>
    runAction("mca", async () => {
      const bundle = selectedBundle(mcaForm.bundle_id);
      if (!bundle) throw new Error("Select a oneM2M config bundle first.");

      const origin = bundle.ae_id;
      const target =
        mcaForm.target ||
        bundle.default_container_resource_id ||
        bundle.default_container_name ||
        "";
      const ri = requestId("mca-ui");
      const m2mHeaders = {
        ...headers,
        "X-M2M-Origin": origin,
        "X-M2M-RI": ri,
        "X-M2M-RVI": "3",
      };

      let response;
      if (mcaForm.operation === "CREATE_AE") {
        const cseTarget =
          bundle.registrar_cse_base_resource_id ||
          cseName(bundle.registrar_cse_base_resource_name) ||
          "in-cse";
        response = await axios.post(
          `${ONEM2M_API}/mca`,
          {
            op: 1,
            to: cseTarget,
            fr: origin,
            rvi: "3",
            ty: 2,
            rqi: ri,
            pc: {
              "m2m:ae": {
                rn: origin,
                api: bundle.app_id,
                rr: true,
                srv: ["3"],
                lbl: ["dashboard-test", "trackcore"],
              },
            },
          },
          {
            headers: { ...m2mHeaders, "Content-Type": "application/json;ty=2" },
            timeout: 16000,
          },
        );
      } else if (mcaForm.operation === "CREATE_CONTAINER") {
        const containerName = mcaForm.container_name || "telemetry-ui";
        response = await axios.post(
          `${ONEM2M_API}/mca`,
          {
            op: 1,
            to: origin,
            fr: origin,
            rvi: "3",
            ty: 3,
            rqi: ri,
            pc: {
              "m2m:cnt": {
                rn: containerName,
                lbl: ["dashboard-test", "telemetry"],
                mni: 10000,
              },
            },
          },
          {
            headers: { ...m2mHeaders, "Content-Type": "application/json;ty=3" },
            timeout: 16000,
          },
        );
      } else if (mcaForm.operation === "CREATE_CIN") {
        const parsedPayload = safeJsonParse(mcaForm.content_json);
        const content =
          parsedPayload?.ts === "auto"
            ? { ...parsedPayload, ts: new Date().toISOString() }
            : parsedPayload;
        response = await axios.post(
          `${ONEM2M_API}/mca/${String(target).replace(/^\/+/, "")}`,
          {
            op: 1,
            to: target,
            fr: origin,
            rvi: "3",
            ty: 4,
            rqi: ri,
            pc: {
              "m2m:cin": {
                rn: `cin-${Date.now()}`,
                cnf: "application/json",
                con: content,
              },
            },
          },
          {
            headers: { ...m2mHeaders, "Content-Type": "application/json;ty=4" },
            timeout: 16000,
          },
        );
      } else if (mcaForm.operation === "RETRIEVE") {
        if (!target) throw new Error("Enter a target RI/path to retrieve.");
        response = await axios.get(
          `${ONEM2M_API}/mca/${String(target).replace(/^\/+/, "")}`,
          {
            headers: m2mHeaders,
            timeout: 16000,
          },
        );
      } else if (mcaForm.operation === "UPDATE_CONTAINER") {
        if (!target)
          throw new Error("Enter a target container RI/path to update.");
        response = await axios.patch(
          `${ONEM2M_API}/mca/${String(target).replace(/^\/+/, "")}`,
          {
            op: 3,
            to: target,
            fr: origin,
            rvi: "3",
            ty: 3,
            rqi: ri,
            pc: {
              "m2m:cnt": {
                lbl: ["dashboard-updated", "telemetry"],
                mni: 10000,
              },
            },
          },
          {
            headers: { ...m2mHeaders, "Content-Type": "application/json;ty=3" },
            timeout: 16000,
          },
        );
      } else if (mcaForm.operation === "DELETE_RESOURCE") {
        if (!mcaForm.target) {
          throw new Error(
            "Enter an explicit target RI/path before running delete.",
          );
        }
        response = await axios.delete(
          `${ONEM2M_API}/mca/${String(mcaForm.target).replace(/^\/+/, "")}`,
          { headers: m2mHeaders, timeout: 16000 },
        );
      }

      setMcaResult(response?.data || {});
      setSuccess("Mca request completed and logged.");
      await loadData();
    });

  const currentMcaAction = MCA_ACTIONS.find(
    (action) => action.value === mcaForm.operation,
  );

  return (
    <Box sx={{ maxWidth: 1720, mx: "auto", width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 5,
          color: "#F8FAFC",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
          mb: 3,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={3}
        >
          <Stack spacing={2} sx={{ maxWidth: 940 }}>
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 28, md: 40 },
                  lineHeight: 0.94,
                  fontWeight: 700,
                  letterSpacing: "-0.06em",
                }}
              >
                OneM2M Service Console
              </Typography>
              <Typography sx={{ mt: 2, color: "#DDEBFF", fontSize: 18 }}>
                Configure service profiles, generate device bundles, test HTTP
                Mca primitives, and inspect resources/logs without leaving the
                dashboard.
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{
                "& .MuiChip-root": {
                  color: "#fff",
                  borderColor: "#fff",
                },
              }}
            >
              <Chip label={`Release ${data.health?.release_version || "3"}`} />
              <Chip label="HTTP Mca" />
              <Chip label="MQTT Mca" />
              <Chip label="Cert based MQTTS ready" />
            </Stack>
          </Stack>
          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RefreshRoundedIcon />
              )
            }
            onClick={loadData}
            disabled={loading}
            sx={{
              px: 2.2,

                      py: 1.1,

                      borderRadius: 999,

                      textTransform: "none",
                      whiteSpace:"nowrap",

                      fontWeight: 700,

                      color: "#082F49",

                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",

                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",

                    }}
          >
            Refresh Console
          </Button>
        </Stack>
      </Paper>

      {error ? (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(5, minmax(0, 1fr))",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <MetricCard
          icon={<HubRoundedIcon />}
          label="Active Profiles"
          value={metrics.activeProfiles}
          helper="Registrar/CSE profiles"
          tone="#1D4ED8"
        />
        <MetricCard
          icon={<DeviceHubRoundedIcon />}
          label="Config Bundles"
          value={metrics.activeBundles}
          helper={`${metrics.devices} dashboard devices loaded`}
          tone="#0F766E"
        />
        <MetricCard
          icon={<AccountTreeRoundedIcon />}
          label="AE Resources"
          value={metrics.aeResources}
          helper="Registered apps/devices"
          tone="#7C3AED"
        />
        <MetricCard
          icon={<ReceiptLongRoundedIcon />}
          label="Content Instances"
          value={metrics.cinResources}
          helper="Telemetry records"
          tone="#EA580C"
        />
        <MetricCard
          icon={<WarningAmberRoundedIcon />}
          label="Request Issues"
          value={metrics.failedRequests}
          helper="Recent failed Mca requests"
          tone="#DC2626"
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 5,
          border: "1px solid rgba(148,163,184,0.22)",
          overflow: "hidden",
          bgcolor: "#FFFFFF",
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, next) => setTab(next)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 900,
                minHeight: 56,
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Phase 1 Actions" />
            <Tab label="Config Bundles" />
            <Tab label="Resource Tree" />
            <Tab label="Certificates" />
            <Tab label="Request Logs" />
            <Tab label="Simulator Guide" />
          </Tabs>
        </Box>
        <Divider />

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <TabPanel value={tab} index={0}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1.15fr 0.85fr" },
                gap: 2.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid rgba(148,163,184,0.22)",
                  bgcolor: "#F8FAFC",
                }}
              >
                <Typography sx={{ fontWeight: 950, fontSize: 26, mb: 1 }}>
                  Phase 1 Lifecycle
                </Typography>
                <Typography sx={{ color: "#64748B", mb: 3 }}>
                  The console now maps the common-service layer from registrar
                  setup to telemetry ingestion so you can test each step
                  deliberately.
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(5, minmax(0, 1fr))",
                    },
                    gap: 1.2,
                  }}
                >
                  {[
                    [
                      "Service Profile",
                      "Defines CSE, App-ID, HTTP/MQTT registrar URLs.",
                    ],
                    [
                      "Config Bundle",
                      "Binds a platform device to AE-ID, node ID, topics.",
                    ],
                    [
                      "Certificate",
                      "Registers cert metadata for cert-based MQTTS policy checks.",
                    ],
                    [
                      "Mca Primitive",
                      "Creates/retrieves/updates/deletes resources through HTTP.",
                    ],
                    [
                      "Resource Logs",
                      "Tracks oneM2M resources, telemetry, and request results.",
                    ],
                  ].map(([title, body], index) => (
                    <Box
                      key={title}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid rgba(37,99,235,0.15)",
                        bgcolor: "#FFFFFF",
                        minHeight: 132,
                      }}
                    >
                      <Typography sx={{ color: "#2563EB", fontWeight: 900 }}>
                        {String(index + 1).padStart(2, "0")}
                      </Typography>
                      <Typography sx={{ fontWeight: 900, mt: 1 }}>
                        {title}
                      </Typography>
                      <Typography
                        sx={{ color: "#64748B", fontSize: 13, mt: 0.8,overflowWrap: "break-word" }}
                      >
                        {body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid rgba(148,163,184,0.22)",
                }}
              >
                <Typography sx={{ fontWeight: 950, fontSize: 24, mb: 2 }}>
                  Current Registrar
                </Typography>
                {(data.profiles || []).length ? (
                  <Stack spacing={1.5}>
                    {data.profiles.slice(0, 3).map((profile) => (
                      <Box
                        key={profile.id}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: "1px solid rgba(148,163,184,0.22)",
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={2}
                        >
                          <Typography sx={{ fontWeight: 900 }}>
                            {profile.provider_name}
                          </Typography>
                          {statusChip(profile.service_state)}
                        </Stack>
                        <Typography sx={{ color: "#64748B", mt: 0.5 }}>
                          {profile.app_id}
                        </Typography>
                        <Typography
                          sx={{ color: "#0F172A", mt: 1, fontWeight: 700 }}
                        >
                          {profile.cse_id} / {profile.cse_base_resource_name}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <EmptyState
                    title="No service profile yet"
                    body="Create one in Phase 1 Actions or generate a default profile through bundle generation."
                  />
                )}
              </Paper>
            </Box>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Stack spacing={2.5}>
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                Use this tab as your oneM2M phase-1 test bench: create the
                registrar profile, generate a device bundle, optionally record
                certificate metadata, then run HTTP Mca smoke operations.
              </Alert>

              {!canManageOrg ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Your current role can view and generate device bundles, but
                  creating service profiles or certificate metadata requires
                  organization.update.
                </Alert>
              ) : null}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
                  gap: 2.5,
                }}
              >
                <ActionCard
                  icon={<AddCircleRoundedIcon />}
                  eyebrow="Step 1"
                  title="Create Service Profile"
                  body="Defines the CSEBase, App-ID, registrar URL, MQTT URL, and security mode."
                  footer={
                    <Button
                      variant="contained"
                      disabled={!canManageOrg || actionBusy === "profile"}
                      onClick={handleCreateProfile}
                      startIcon={
                        actionBusy === "profile" ? (
                          <CircularProgress size={16} />
                        ) : null
                      }
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                    >
                      Create Profile
                    </Button>
                  }
                >
                  <FieldGrid columns={2}>
                    <TextField
                      label="Provider name"
                      size="small"
                      value={profileForm.provider_name}
                      onChange={setFormValue(setProfileForm, "provider_name")}
                    />
                    <TextField
                      label="Provider code"
                      size="small"
                      value={profileForm.provider_code}
                      onChange={setFormValue(setProfileForm, "provider_code")}
                    />
                    <TextField
                      label="App-ID"
                      size="small"
                      value={profileForm.app_id}
                      onChange={setFormValue(setProfileForm, "app_id")}
                      helperText="Example: Ntrackcore.customer.default"
                    />
                    <TextField
                      label="CSE ID"
                      size="small"
                      value={profileForm.cse_id}
                      onChange={setFormValue(setProfileForm, "cse_id")}
                    />
                    <TextField
                      label="CSEBase RI"
                      size="small"
                      value={profileForm.cse_base_resource_id}
                      onChange={setFormValue(
                        setProfileForm,
                        "cse_base_resource_id",
                      )}
                    />
                    <TextField
                      label="CSEBase name"
                      size="small"
                      value={profileForm.cse_base_resource_name}
                      onChange={setFormValue(
                        setProfileForm,
                        "cse_base_resource_name",
                      )}
                    />
                    <TextField
                      label="Registrar HTTP URL"
                      size="small"
                      value={profileForm.registrar_url}
                      onChange={setFormValue(setProfileForm, "registrar_url")}
                    />
                    <TextField
                      label="Registrar MQTT URL"
                      size="small"
                      value={profileForm.registrar_mqtt_url}
                      onChange={setFormValue(
                        setProfileForm,
                        "registrar_mqtt_url",
                      )}
                    />
                    <TextField
                      select
                      label="Security mode"
                      size="small"
                      value={profileForm.security_mode}
                      onChange={setFormValue(setProfileForm, "security_mode")}
                    >
                      <MenuItem value="CERT_TLS">CERT_TLS</MenuItem>
                      <MenuItem value="TLS">TLS</MenuItem>
                      <MenuItem value="PLAINTEXT">PLAINTEXT</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label="Service state"
                      size="small"
                      value={profileForm.service_state}
                      onChange={setFormValue(setProfileForm, "service_state")}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                    </TextField>
                  </FieldGrid>
                </ActionCard>

                <ActionCard
                  icon={<SettingsEthernetRoundedIcon />}
                  eyebrow="Step 2"
                  title="Generate Device Config Bundle"
                  body="Produces the exact bundle firmware needs: AE-ID, node ID, Mca paths, MQTT topics, and cert SAN."
                  footer={
                    <Button
                      variant="contained"
                      disabled={actionBusy === "bundle"}
                      onClick={handleGenerateBundle}
                      startIcon={
                        actionBusy === "bundle" ? (
                          <CircularProgress size={16} />
                        ) : null
                      }
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                    >
                      Generate Bundle
                    </Button>
                  }
                >
                  <FieldGrid columns={2}>
                    <TextField
                      select
                      label="Device"
                      size="small"
                      value={bundleForm.device_id}
                      onChange={setFormValue(setBundleForm, "device_id")}
                      helperText={
                        data.devices?.length
                          ? "Any registered/provisioned device in this workspace"
                          : "No devices loaded from Device Management"
                      }
                    >
                      {(data.devices || []).map((device) => (
                        <MenuItem key={device.id} value={device.id}>
                          {deviceLabel(device)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Service profile"
                      size="small"
                      value={bundleForm.service_profile_id}
                      onChange={setFormValue(
                        setBundleForm,
                        "service_profile_id",
                      )}
                      helperText="Leave blank only if backend should create the default profile."
                    >
                      <MenuItem value="">Use default profile</MenuItem>
                      {(data.profiles || []).map((profile) => (
                        <MenuItem key={profile.id} value={profile.id}>
                          {profileLabel(profile)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Config version"
                      size="small"
                      value={bundleForm.config_version}
                      onChange={setFormValue(setBundleForm, "config_version")}
                    />
                    <TextField
                      label="AE-ID override"
                      size="small"
                      value={bundleForm.ae_id}
                      onChange={setFormValue(setBundleForm, "ae_id")}
                      helperText="Optional. Backend generates one if empty."
                    />
                    <TextField
                      label="Default container"
                      size="small"
                      value={bundleForm.default_container_name}
                      onChange={setFormValue(
                        setBundleForm,
                        "default_container_name",
                      )}
                    />
                    <TextField
                      select
                      label="Force new"
                      size="small"
                      value={bundleForm.force_new}
                      onChange={setFormValue(setBundleForm, "force_new")}
                    >
                      <MenuItem value="false">No, reuse active bundle</MenuItem>
                      <MenuItem value="true">Yes, regenerate</MenuItem>
                    </TextField>
                  </FieldGrid>
                </ActionCard>

                <ActionCard
                  icon={<VpnKeyRoundedIcon />}
                  eyebrow="Step 3"
                  title="Register Certificate Metadata"
                  body="Stores certificate identity/status so cert-based MQTTS can be enforced and audited."
                  footer={
                    <Button
                      variant="contained"
                      disabled={!canManageOrg || actionBusy === "certificate"}
                      onClick={handleRegisterCertificate}
                      startIcon={
                        actionBusy === "certificate" ? (
                          <CircularProgress size={16} />
                        ) : null
                      }
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                    >
                      Register Certificate
                    </Button>
                  }
                >
                  <FieldGrid columns={2}>
                    <TextField
                      select
                      label="Source bundle"
                      size="small"
                      value={certForm.bundle_id}
                      onChange={(event) =>
                        applyBundleToCert(event.target.value)
                      }
                    >
                      <MenuItem value="">Select bundle</MenuItem>
                      {(data.bundles || []).map((bundle) => (
                        <MenuItem key={bundle.id} value={bundle.id}>
                          {bundle.ae_id} / {compact(bundle.device_identifier)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Status"
                      size="small"
                      value={certForm.status}
                      onChange={setFormValue(setCertForm, "status")}
                    >
                      <MenuItem value="issued">Issued</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                      <MenuItem value="revoked">Revoked</MenuItem>
                    </TextField>
                    <TextField
                      label="AE-ID"
                      size="small"
                      value={certForm.ae_id}
                      onChange={setFormValue(setCertForm, "ae_id")}
                    />
                    <TextField
                      label="Subject Alt Name"
                      size="small"
                      value={certForm.subject_alt_name}
                      onChange={setFormValue(setCertForm, "subject_alt_name")}
                    />
                    <TextField
                      label="Certificate serial"
                      size="small"
                      value={certForm.certificate_serial_number}
                      onChange={setFormValue(
                        setCertForm,
                        "certificate_serial_number",
                      )}
                    />
                    <TextField
                      label="SHA-256 fingerprint"
                      size="small"
                      value={certForm.fingerprint_sha256}
                      onChange={setFormValue(setCertForm, "fingerprint_sha256")}
                    />
                    <TextField
                      label="Expires at"
                      type="datetime-local"
                      size="small"
                      value={certForm.expires_at}
                      onChange={setFormValue(setCertForm, "expires_at")}
                      InputLabelProps={{ shrink: true }}
                    />
                  </FieldGrid>
                </ActionCard>

                <ActionCard
                  icon={<ScienceRoundedIcon />}
                  eyebrow="Step 4"
                  title="Run HTTP Mca Smoke Test"
                  body="Tests oneM2M CREATE, RETRIEVE, UPDATE, and DELETE primitives using the selected AE-ID."
                  footer={
                    <Stack spacing={1.5}>
                      <Button
                        variant="contained"
                        disabled={actionBusy === "mca"}
                        onClick={handleRunMca}
                        startIcon={
                          actionBusy === "mca" ? (
                            <CircularProgress size={16} />
                          ) : null
                        }
                        sx={{
                          borderRadius: 999,
                          textTransform: "none",
                          fontWeight: 900,
                        }}
                      >
                        Run Mca Test
                      </Button>
                      {mcaResult ? (
                        <CodeBlock value={JSON.stringify(mcaResult, null, 2)} />
                      ) : null}
                    </Stack>
                  }
                >
                  <FieldGrid columns={2}>
                    <TextField
                      select
                      label="Source bundle"
                      size="small"
                      value={mcaForm.bundle_id}
                      onChange={(event) => applyBundleToMca(event.target.value)}
                    >
                      <MenuItem value="">Select bundle</MenuItem>
                      {(data.bundles || []).map((bundle) => (
                        <MenuItem key={bundle.id} value={bundle.id}>
                          {bundle.ae_id} / {compact(bundle.device_identifier)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Operation"
                      size="small"
                      value={mcaForm.operation}
                      onChange={setFormValue(setMcaForm, "operation")}
                      helperText={currentMcaAction?.helper || ""}
                    >
                      {MCA_ACTIONS.map((action) => (
                        <MenuItem key={action.value} value={action.value}>
                          {action.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Target RI/path"
                      size="small"
                      value={mcaForm.target}
                      onChange={setFormValue(setMcaForm, "target")}
                      helperText="Defaults to the bundle telemetry container."
                    />
                    <TextField
                      label="Container name"
                      size="small"
                      value={mcaForm.container_name}
                      onChange={setFormValue(setMcaForm, "container_name")}
                      helperText="Used only for Create test container."
                    />
                  </FieldGrid>
                  <TextField
                    label="ContentInstance sample JSON"
                    multiline
                    minRows={7}
                    value={mcaForm.content_json}
                    onChange={setFormValue(setMcaForm, "content_json")}
                    helperText="Used only for Create content instance. Keep ts as auto to stamp the current time."
                  />
                </ActionCard>
              </Box>
            </Stack>
          </TabPanel>

          <TabPanel value={tab} index={2}>
            {(data.bundles || []).length ? (
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>AE ID</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>Container</TableCell>
                      <TableCell>HTTP Path</TableCell>
                      <TableCell>MQTT Request Topic</TableCell>
                      <TableCell>Security</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.bundles.map((bundle) => {
                      const topic = bundleTopic(bundle);
                      const httpPath = bundleHttpPath(bundle);
                      return (
                        <TableRow key={bundle.id} hover>
                          <TableCell sx={{ fontWeight: 800 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <span>{compact(bundle.ae_id)}</span>
                              <CopyButton
                                text={bundle.ae_id}
                                label="Copy AE-ID"
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {compact(
                              bundle.device_identifier || bundle.device_id,
                            )}
                          </TableCell>
                          <TableCell>
                            {bundle.default_container_name || "-"}
                            <Typography sx={{ color: "#64748B", fontSize: 12 }}>
                              {bundle.default_container_resource_id || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <Typography sx={{ fontSize: 13 }}>
                                {compact(httpPath, 18, 14)}
                              </Typography>
                              <CopyButton
                                text={httpPath}
                                label="Copy HTTP path"
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <Typography sx={{ fontSize: 13 }}>
                                {compact(topic, 20, 14)}
                              </Typography>
                              <CopyButton text={topic} label="Copy topic" />
                            </Stack>
                          </TableCell>
                          <TableCell>{bundle.security_mode}</TableCell>
                          <TableCell>{statusChip(bundle.status)}</TableCell>
                          <TableCell>
                            {formatDateTime(bundle.created_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyState
                title="No oneM2M config bundles yet"
                body="Create a bundle from a provisioned device to make it visible here."
              />
            )}
          </TabPanel>

          <TabPanel value={tab} index={3}>
            {(data.resources || []).length ? (
              <TableContainer sx={{ maxHeight: 620 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Resource ID</TableCell>
                      <TableCell>Path</TableCell>
                      <TableCell>AE ID</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.resources.map((resource) => (
                      <TableRow key={resource.id} hover>
                        <TableCell>{resource.resource_type_name}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>
                          {resource.resource_name}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <span>{compact(resource.resource_identifier)}</span>
                            <CopyButton
                              text={resource.resource_identifier}
                              label="Copy RI"
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>{resource.structured_path}</TableCell>
                        <TableCell>{compact(resource.ae_id)}</TableCell>
                        <TableCell>{compact(resource.device_id)}</TableCell>
                        <TableCell>
                          {formatDateTime(resource.updated_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyState
                title="No resource tree yet"
                body="Once a device registers or posts telemetry, CSEBase, AE, container, and contentInstance records will appear here."
              />
            )}
          </TabPanel>

          <TabPanel value={tab} index={4}>
            {(data.certificates || []).length ? (
              <TableContainer sx={{ maxHeight: 540 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>AE ID</TableCell>
                      <TableCell>Subject Alt Name</TableCell>
                      <TableCell>Fingerprint</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.certificates.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell sx={{ fontWeight: 800 }}>
                          {compact(record.ae_id)}
                        </TableCell>
                        <TableCell>{record.subject_alt_name}</TableCell>
                        <TableCell>
                          {compact(record.fingerprint_sha256, 14, 10)}
                        </TableCell>
                        <TableCell>
                          {statusChip(record.status, ["issued", "active"])}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(record.expires_at)}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(record.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyState
                title="No certificate records yet"
                body="Certificate metadata will show here once device certificates are issued or registered."
              />
            )}
          </TabPanel>

          <TabPanel value={tab} index={5}>
            {(data.logs || []).length ? (
              <TableContainer sx={{ maxHeight: 620 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Request ID</TableCell>
                      <TableCell>Originator</TableCell>
                      <TableCell>Operation</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell>Resource Type</TableCell>
                      <TableCell>RSC</TableCell>
                      <TableCell>HTTP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>{formatDateTime(log.created_at)}</TableCell>
                        <TableCell>
                          {compact(log.request_identifier, 12, 8)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>
                          {compact(log.originator)}
                        </TableCell>
                        <TableCell>
                          {OPERATION_LABELS[log.operation] ||
                            log.operation ||
                            "-"}
                        </TableCell>
                        <TableCell>{compact(log.target, 26, 14)}</TableCell>
                        <TableCell>{log.resource_type || "-"}</TableCell>
                        <TableCell>{log.response_status_code || "-"}</TableCell>
                        <TableCell>{log.http_status_code || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyState
                title="No Mca request logs yet"
                body="HTTP or MQTT oneM2M requests will be logged after a real AE-ID starts communicating."
              />
            )}
          </TabPanel>

          <TabPanel value={tab} index={6}>
            <Stack spacing={2.5}>
              {!latestBundle ? (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  No active config bundle is available. Generate a bundle for a
                  provisioned device first, then return here for exact commands.
                </Alert>
              ) : null}

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  border: "1px solid rgba(148,163,184,0.22)",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 1.5 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TerminalRoundedIcon sx={{ color: "#2563EB" }} />
                    <Typography sx={{ fontWeight: 950, fontSize: 22 }}>
                      MQTT Simulator Command
                    </Typography>
                  </Stack>
                  <CopyButton text={simulatorCommand} label="Copy command" />
                </Stack>
                <CodeBlock
                  value={
                    simulatorCommand ||
                    "Generate a config bundle to see the simulator command."
                  }
                />
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  border: "1px solid rgba(148,163,184,0.22)",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 1.5 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ReceiptLongRoundedIcon sx={{ color: "#0F766E" }} />
                    <Typography sx={{ fontWeight: 950, fontSize: 22 }}>
                      HTTP Smoke Test
                    </Typography>
                  </Stack>
                  <CopyButton text={httpSmokeCommand} label="Copy command" />
                </Stack>
                <CodeBlock
                  value={
                    httpSmokeCommand ||
                    "Generate a config bundle to see the HTTP test command."
                  }
                />
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  border: "1px solid rgba(148,163,184,0.22)",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <FactCheckRoundedIcon sx={{ color: "#7C3AED" }} />
                  <Typography sx={{ fontWeight: 950, fontSize: 22 }}>
                    Bundle Snapshot
                  </Typography>
                </Stack>
                <CodeBlock
                  value={
                    latestBundle
                      ? JSON.stringify(
                          latestBundle.config || latestBundle,
                          null,
                          2,
                        )
                      : "{}"
                  }
                />
              </Paper>
            </Stack>
          </TabPanel>
        </Box>
      </Paper>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3600}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
